/**
 * Сессия игрока: вход через Telegram, профиль и баланс.
 *
 * Баланс держится здесь, потому что его меняют сразу несколько источников —
 * ставка, вывод, пополнение и события по WebSocket. Один владелец состояния
 * избавляет от расхождений между экранами.
 */

import { create } from "zustand";

import type { UserDto } from "@koshkodrop/shared";

import { ApiError, api, tokens } from "../lib/api";
import {
  clearPendingWelcome,
  getInitData,
  hasPendingWelcome,
  maybeRequestReferralWriteAccess,
} from "../lib/telegram";

type Status = "idle" | "loading" | "ready" | "error" | "needs_channel";

/** Лапки тестовому игроку: хватает на самый дорогой кейс каталога. */
const DEV_BALANCE = "20000000";

interface SessionState {
  status: Status;
  user: UserDto | null;
  balance: bigint;
  error: string | null;
  channel: string | null;
  channelUrl: string | null;
  /** Приложение открыто вне Telegram: играть нельзя, но интерфейс покажем. */
  outsideTelegram: boolean;

  login: (recheck?: boolean) => Promise<void>;
  refreshProfile: () => Promise<void>;
  applyUser: (user: UserDto) => void;
  setBalance: (balance: bigint) => void;
  logout: () => void;
}

export const useSession = create<SessionState>((set, get) => ({
  status: "idle",
  user: null,
  balance: 0n,
  error: null,
  channel: null,
  channelUrl: null,
  outsideTelegram: false,

  async login(recheck = false) {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });

    const initData = getInitData();

    // Вне Telegram initData пустой. Пытаемся восстановить прошлую сессию
    // по сохранённому токену, чтобы разработка была возможна в браузере.
    if (!initData) {
      set({ outsideTelegram: true });
      if (tokens.access || tokens.refresh) {
        try {
          const { user } = await api.me();
          set({ status: "ready", user, balance: BigInt(user.balance) });
          return;
        } catch {
          tokens.clear();
        }
      }
      // В dev-сборке поднимаем тестового игрока: маршрут /auth/dev в бою не
      // существует, а иначе мини-апп нельзя открыть в обычном браузере
      if (import.meta.env.DEV) {
        try {
          const result = await api.loginAsDev(DEV_BALANCE);
          tokens.save(result.accessToken, result.refreshToken);
          set({
            status: "ready",
            user: result.user,
            balance: BigInt(result.user.balance),
            error: null,
          });
          return;
        } catch {
          // Падаем в обычное сообщение: значит API не в режиме разработки
        }
      }

      set({
        status: "error",
        error: "Откройте Кошкодроп через Telegram, чтобы играть",
      });
      return;
    }

    try {
      const result = await api.loginWithTelegram(initData, recheck);
      tokens.save(result.accessToken, result.refreshToken);
      set({
        status: "ready",
        user: result.user,
        balance: BigInt(result.user.balance),
        error: null,
        channel: null,
        channelUrl: null,
      });

      maybeRequestReferralWriteAccess();

      if (hasPendingWelcome()) {
        clearPendingWelcome();
        void api.referralsWelcome().catch(() => {
          // Бот мог отправить приветствие сам через write_access_allowed
        });
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === "channel_required") {
        set({
          status: "needs_channel",
          error: error.message,
          channel: error.details?.channel ?? "@koshkodrop",
          channelUrl: error.details?.channelUrl ?? "https://t.me/koshkodrop",
        });
        return;
      }

      const message =
        error instanceof ApiError ? error.message : "Не удалось войти, попробуйте ещё раз";
      set({ status: "error", error: message });
    }
  },

  async refreshProfile() {
    try {
      const { user } = await api.me();
      set({ user, balance: BigInt(user.balance) });
    } catch (error) {
      if (error instanceof ApiError && error.code === "channel_required") {
        tokens.clear();
        set({
          status: "needs_channel",
          user: null,
          balance: 0n,
          error: error.message,
          channel: error.details?.channel ?? "@koshkodrop",
          channelUrl: error.details?.channelUrl ?? "https://t.me/koshkodrop",
        });
      }
    }
  },

  applyUser(user) {
    set({ user, balance: BigInt(user.balance) });
  },

  setBalance(balance) {
    set({ balance });
  },

  logout() {
    tokens.clear();
    set({
      status: "idle",
      user: null,
      balance: 0n,
      error: null,
      channel: null,
      channelUrl: null,
    });
  },
}));
